import { useEffect, useState, useCallback } from 'react';
import { supabase } from './supabase';

// Same set the mobile app uses — keep the two in sync so a
// reaction added from web reads the same as one added from iOS.
export const QUICK_REACTIONS = ['👍', '🔥', '💪', '❤️'];

/**
 * Inbox for the current user. Mirrors the mobile lib's
 * `loadInbox` shape so the UI components can share the row
 * model. Returns one row per channel the user participates in,
 * sorted by activity, with the latest-message preview.
 */
export async function loadInbox(userId) {
  const { data: parts, error } = await supabase
    .from('channel_participants')
    .select(`
      role,
      last_read_at,
      channel:channels (
        id, type, name, replies_disabled, flagged, created_at, last_message_at, created_by
      )
    `)
    .eq('user_id', userId);
  if (error) throw error;

  const channels = (parts ?? [])
    .map(p => p.channel ? { ...p.channel, my_role: p.role, last_read_at: p.last_read_at } : null)
    .filter(Boolean);
  if (!channels.length) return [];

  const channelIds = channels.map(c => c.id);

  const [{ data: allParts }, { data: latestPerChannel }] = await Promise.all([
    supabase
      .from('channel_participants')
      .select('channel_id, user_id, role')
      .in('channel_id', channelIds),
    supabase
      .from('messages')
      .select('id, channel_id, sender_id, content, created_at')
      .in('channel_id', channelIds)
      .order('created_at', { ascending: false })
      .limit(200),
  ]);

  // Names live on `profiles`; avatar_url lives on whichever
  // detail table exists for that user (athlete_profiles for
  // athletes, coach_profiles for coaches). Pull both in parallel
  // so the inbox can render the same photos the coach detail
  // page already shows.
  const userIds = [...new Set((allParts ?? []).map(p => p.user_id))];
  const [{ data: profiles }, { data: athleteAv }, { data: coachAv }] = await Promise.all([
    supabase.from('profiles').select('id, full_name').in('id', userIds),
    supabase.from('athlete_profiles').select('id, avatar_url').in('id', userIds),
    supabase.from('coach_profiles').select('id, avatar_url').in('id', userIds),
  ]);
  const nameById = Object.fromEntries((profiles ?? []).map(p => [p.id, p.full_name ?? '']));
  const avatarById = {};
  (athleteAv ?? []).forEach(r => { if (r.avatar_url) avatarById[r.id] = r.avatar_url; });
  (coachAv ?? []).forEach(r => { if (r.avatar_url) avatarById[r.id] = r.avatar_url; });

  const partsByChannel = {};
  (allParts ?? []).forEach(p => {
    if (!partsByChannel[p.channel_id]) partsByChannel[p.channel_id] = [];
    partsByChannel[p.channel_id].push({
      user_id: p.user_id,
      full_name: nameById[p.user_id] ?? '',
      avatar_url: avatarById[p.user_id] ?? null,
      role: p.role,
    });
  });

  const lastByChannel = {};
  (latestPerChannel ?? []).forEach(m => {
    if (!lastByChannel[m.channel_id]) lastByChannel[m.channel_id] = m;
  });

  return channels
    .map(ch => ({
      ...ch,
      participants: partsByChannel[ch.id] ?? [],
      other_participants: (partsByChannel[ch.id] ?? []).filter(p => p.user_id !== userId),
      last_message: lastByChannel[ch.id] ?? null,
    }))
    .sort((a, b) => {
      const ta = new Date(a.last_message_at ?? a.created_at).getTime();
      const tb = new Date(b.last_message_at ?? b.created_at).getTime();
      return tb - ta;
    });
}

export async function loadThread(channelId, userId) {
  const [{ data: channel }, { data: myPart }, { data: messages }] = await Promise.all([
    supabase.from('channels').select('*').eq('id', channelId).maybeSingle(),
    userId
      ? supabase
          .from('channel_participants')
          .select('role, last_read_at')
          .eq('channel_id', channelId)
          .eq('user_id', userId)
          .maybeSingle()
      : Promise.resolve({ data: null }),
    supabase
      .from('messages')
      .select('id, channel_id, sender_id, content, metadata, created_at, edited_at')
      .eq('channel_id', channelId)
      .order('created_at', { ascending: true })
      .limit(500),
  ]);

  // Always pull all participants too so the thread header can
  // show names + roles regardless of whether the viewer is one of
  // them (admins reviewing other people's channels).
  const { data: allParts } = await supabase
    .from('channel_participants')
    .select('user_id, role, last_read_at')
    .eq('channel_id', channelId);

  const messageIds = (messages ?? []).map(m => m.id);
  const userIds = [
    ...new Set([
      ...(messages ?? []).map(m => m.sender_id),
      ...(allParts ?? []).map(p => p.user_id),
    ]),
  ];

  const [{ data: reactions }, { data: profiles }, { data: athleteAv }, { data: coachAv }] = await Promise.all([
    messageIds.length
      ? supabase
          .from('message_reactions')
          .select('id, message_id, user_id, emoji, created_at')
          .in('message_id', messageIds)
      : Promise.resolve({ data: [] }),
    userIds.length
      ? supabase.from('profiles').select('id, full_name, email').in('id', userIds)
      : Promise.resolve({ data: [] }),
    userIds.length
      ? supabase.from('athlete_profiles').select('id, avatar_url').in('id', userIds)
      : Promise.resolve({ data: [] }),
    userIds.length
      ? supabase.from('coach_profiles').select('id, avatar_url').in('id', userIds)
      : Promise.resolve({ data: [] }),
  ]);

  const profileById = Object.fromEntries((profiles ?? []).map(p => [p.id, p]));
  const avatarById = {};
  (athleteAv ?? []).forEach(r => { if (r.avatar_url) avatarById[r.id] = r.avatar_url; });
  (coachAv ?? []).forEach(r => { if (r.avatar_url) avatarById[r.id] = r.avatar_url; });

  return {
    channel,
    myRole: myPart?.role ?? null,
    lastReadAt: myPart?.last_read_at ?? null,
    messages: messages ?? [],
    reactions: reactions ?? [],
    participants: (allParts ?? []).map(p => ({
      user_id: p.user_id,
      role: p.role,
      full_name: profileById[p.user_id]?.full_name ?? '',
      email: profileById[p.user_id]?.email ?? '',
      avatar_url: avatarById[p.user_id] ?? null,
    })),
    profileById,
    avatarById,
  };
}

export async function sendMessage({ channelId, senderId, content }) {
  const { data, error } = await supabase
    .from('messages')
    .insert({ channel_id: channelId, sender_id: senderId, content })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function toggleReaction({ messageId, userId, emoji }) {
  const { data: existing } = await supabase
    .from('message_reactions')
    .select('id')
    .eq('message_id', messageId)
    .eq('user_id', userId)
    .eq('emoji', emoji)
    .maybeSingle();
  if (existing) {
    const { error } = await supabase
      .from('message_reactions')
      .delete()
      .eq('id', existing.id);
    if (error) throw error;
    return { added: false };
  }
  const { error } = await supabase
    .from('message_reactions')
    .insert({ message_id: messageId, user_id: userId, emoji });
  if (error) throw error;
  return { added: true };
}

export async function markChannelRead(channelId, userId) {
  if (!channelId || !userId) return;
  await supabase
    .from('channel_participants')
    .update({ last_read_at: new Date().toISOString() })
    .eq('channel_id', channelId)
    .eq('user_id', userId);
}

/**
 * Channel creation. Inserts the channel row, then the participant
 * rows for the creator + invitees in one round-trip. The creator
 * is always given role='admin' so they can manage the channel
 * later (rename, lock replies, add/remove participants).
 *
 * For announcements: invitees default to 'viewer' so the read-only
 * spec applies. Caller can override per row if needed.
 */
export async function createChannel({
  type, name, createdBy, participants,
}) {
  const { data: channel, error } = await supabase
    .from('channels')
    .insert({ type, name: name || null, created_by: createdBy })
    .select()
    .single();
  if (error) throw error;

  const rows = [
    { channel_id: channel.id, user_id: createdBy, role: 'admin' },
    ...participants
      .filter(p => p.user_id !== createdBy)
      .map(p => ({
        channel_id: channel.id,
        user_id: p.user_id,
        role: p.role ?? (type === 'announcement' ? 'viewer' : 'member'),
      })),
  ];
  const { error: pErr } = await supabase.from('channel_participants').insert(rows);
  if (pErr) {
    // Best-effort cleanup so a failed participant insert doesn't
    // leave an orphaned channel that the creator can't see.
    await supabase.from('channels').delete().eq('id', channel.id);
    throw pErr;
  }
  return channel;
}

export async function updateChannel(channelId, patch) {
  const { error } = await supabase
    .from('channels')
    .update(patch)
    .eq('id', channelId);
  if (error) throw error;
}

export async function addParticipants(channelId, rows) {
  const { error } = await supabase
    .from('channel_participants')
    .insert(rows.map(r => ({ channel_id: channelId, ...r })));
  if (error) throw error;
}

export async function removeParticipant(channelId, userId) {
  const { error } = await supabase
    .from('channel_participants')
    .delete()
    .eq('channel_id', channelId)
    .eq('user_id', userId);
  if (error) throw error;
}

/**
 * Admin Safety Review — list every channel with metadata only.
 * No message bodies are loaded by this query; opening a channel
 * for review goes through `logAdminAccess` first. RLS on channels
 * passes for app admins (`is_app_admin()`).
 */
export async function loadAllChannelsForAdmin() {
  const { data: channels, error } = await supabase
    .from('channels')
    .select('id, type, name, replies_disabled, flagged, created_at, last_message_at, created_by')
    .order('last_message_at', { ascending: false, nullsFirst: false });
  if (error) throw error;
  if (!channels?.length) return [];

  const channelIds = channels.map(c => c.id);
  const { data: parts } = await supabase
    .from('channel_participants')
    .select('channel_id, user_id, role')
    .in('channel_id', channelIds);
  const userIds = [...new Set((parts ?? []).map(p => p.user_id))];
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, full_name, email')
    .in('id', userIds);
  const profileById = Object.fromEntries((profiles ?? []).map(p => [p.id, p]));

  const partsByChannel = {};
  (parts ?? []).forEach(p => {
    if (!partsByChannel[p.channel_id]) partsByChannel[p.channel_id] = [];
    partsByChannel[p.channel_id].push({
      user_id: p.user_id,
      role: p.role,
      full_name: profileById[p.user_id]?.full_name ?? '',
      email: profileById[p.user_id]?.email ?? '',
    });
  });

  // Counts: how many messages, last activity. We already have
  // last_message_at on the row; message_count needs an aggregate.
  const { data: counts } = await supabase
    .from('messages')
    .select('channel_id', { count: 'exact', head: false })
    .in('channel_id', channelIds);
  const countByChannel = {};
  (counts ?? []).forEach(m => {
    countByChannel[m.channel_id] = (countByChannel[m.channel_id] ?? 0) + 1;
  });

  return channels.map(c => ({
    ...c,
    participants: partsByChannel[c.id] ?? [],
    message_count: countByChannel[c.id] ?? 0,
  }));
}

export async function logAdminAccess({ adminId, channelId, reason }) {
  const { data, error } = await supabase
    .from('admin_message_access_log')
    .insert({ admin_id: adminId, channel_id: channelId, reason: reason.trim() })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function loadAdminAccessLog({ channelId, adminId, limit = 50 }) {
  let q = supabase
    .from('admin_message_access_log')
    .select('id, admin_id, channel_id, reason, accessed_at')
    .order('accessed_at', { ascending: false })
    .limit(limit);
  if (channelId) q = q.eq('channel_id', channelId);
  if (adminId)   q = q.eq('admin_id', adminId);
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

/**
 * Polled unread-count for the coach sidebar badge. Mirrors the
 * mobile hook's behavior (60s tick) so the two stay in sync.
 */
export function useUnreadChannelsCount(userId) {
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!userId) return;
    const { data } = await supabase
      .from('channel_participants')
      .select(`
        last_read_at,
        channel:channels ( last_message_at )
      `)
      .eq('user_id', userId);
    const unread = (data ?? []).reduce((n, row) => {
      const lastMsg = row.channel?.last_message_at;
      if (!lastMsg) return n;
      const seen = row.last_read_at;
      return !seen || new Date(seen) < new Date(lastMsg) ? n + 1 : n;
    }, 0);
    setCount(unread);
  }, [userId]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, [refresh]);

  return { count, refresh };
}

export function groupReactions(reactionsForMessage, currentUserId) {
  const buckets = new Map();
  for (const r of reactionsForMessage) {
    if (!buckets.has(r.emoji)) buckets.set(r.emoji, { emoji: r.emoji, count: 0, mine: false });
    const b = buckets.get(r.emoji);
    b.count += 1;
    if (r.user_id === currentUserId) b.mine = true;
  }
  return Array.from(buckets.values()).sort((a, b) => b.count - a.count);
}

export function canSendMessages({ channel, myRole }) {
  if (!channel || !myRole) return false;
  if (myRole === 'viewer') return false;
  if (channel.replies_disabled && myRole !== 'admin') return false;
  return true;
}

export function canReact({ myRole }) {
  return !!myRole;
}

/**
 * Read-receipt summary for a single message — mirror of the
 * mobile helper. Returns null when the message wasn't sent by
 * the current user, or when the channel has no other readers.
 */
export function summarizeReadState({ message, channel, participants, currentUserId }) {
  if (!message || message.sender_id !== currentUserId) return null;
  if (!channel) return null;
  const others = (participants ?? []).filter(p => p.user_id !== currentUserId);
  if (!others.length) return null;
  const sentAt = new Date(message.created_at).getTime();
  const readers = others.filter(p =>
    p.last_read_at && new Date(p.last_read_at).getTime() >= sentAt
  );
  if (channel.type === 'direct') {
    if (readers.length > 0) {
      const t = new Date(readers[0].last_read_at);
      return {
        kind: 'direct-read',
        label: `Read ${t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`,
      };
    }
    return { kind: 'delivered', label: 'Delivered' };
  }
  return {
    kind: 'group-count',
    label: readers.length === 0
      ? 'Delivered'
      : `Read by ${readers.length} of ${others.length}`,
    readers,
    others,
  };
}

/**
 * Human-readable channel label. Direct = the other person's
 * name; group/announcement = the channel's name; falls back to
 * type if unnamed.
 */
export function channelLabel(channel, currentUserId) {
  if (!channel) return '';
  if (channel.type === 'direct') {
    const other = (channel.other_participants ?? channel.participants ?? [])
      .find(p => p.user_id !== currentUserId);
    return other?.full_name || other?.email || 'Direct message';
  }
  return channel.name || (channel.type === 'announcement' ? 'Announcement' : 'Group');
}
